import { colors } from 'bench-style-constants';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import ReactSelect, { components } from 'react-select';
import { rgba } from 'color-helpers';
import styled from 'styled-components';

import Icon, { iconPaths } from './Icon';
import TextInput from './TextInput';

/**
 * Represents the variances between the two differently sized combo boxes in the internal component
 * library.
 */
const sizes = {
  small: {
    buttonPadding: '3px',
    height: '24px',
  },
  medium: {
    buttonPadding: '8px',
    height: '32px',
  },
};

const clearIconURL = encodeURIComponent(`
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path fill="${colors.dust}" d="${iconPaths.close}" />
  </svg>
`);

const ClearButton = styled.button`
  background: ${colors.sand} url('data:image/svg+xml;utf8,${clearIconURL}') 50% 50% no-repeat;
  border: 0 none;
  border-radius: 8px;
  box-shadow: none;
  cursor: pointer;
  display: inline-block;
  height: 16px;
  outline: none;
  padding: 0;
  width: 16px;
`;

/**
 * A hidden text input is placed beneath the Combo Box Input to utilize the HTML Form Validation for
 * required values since React-Select does not currently support this feature. This wrapper is used
 * to insert the text input beneath the Combo Box Input
 */
const TextInputWrapper = styled.div`
  [data-visually-hidden] {
    position: absolute;
    height: 1px;
    width: 1px;
    overflow: hidden;
    clip: rect(1px, 1px, 1px, 1px);
  }
`;

/**
 * Returns an object as required by the ReactSelect custom styles API.
 *
 * @see https://react-select.com/styles
 */
const customStyles = (iconDecoration, size, required, wasValidated, errorText) => ({
  clearIndicator: provided => ({
    ...provided,
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: sizes[size].buttonPadding,
    paddingTop: '0',
  }),
  container: provided => ({
    ...provided,
    boxSizing: 'border-box',
    fontSize: '12px',
  }),
  control: (provided, { isFocused, hasValue, isDisabled }) => {
    const isInvalid = (wasValidated && required && !hasValue) || !!errorText;
    return {
      ...provided,
      background: isDisabled ? colors.sand : colors.white,
      borderColor: (isDisabled && colors.sand) || (isInvalid && colors.copper) || colors.stone,
      boxShadow:
        (isInvalid && isFocused && `0 0 4px 1px ${colors.copper}`) ||
        (isFocused && `0 0 4px 1px ${rgba(colors.creek, 0.65)}`) ||
        'none',
      '&:hover': {
        borderColor: isInvalid ? colors.copper : colors.stone,
      },
    };
  },
  dropdownIndicator: provided => ({
    ...provided,
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: sizes[size].buttonPadding,
    paddingTop: '0',
  }),
  indicatorSeparator: provided => ({
    ...provided,
    display: 'none',
  }),
  menu: provided => ({
    ...provided,
    border: `1px solid ${colors.stone}`,
    borderRadius: '2px',
    boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.15)',
    fontSize: '12px',
  }),
  option: (provided, { isDisabled, isFocused }) => ({
    ...provided,
    backgroundColor: !isDisabled && isFocused ? '#DFF0F0' : null,
    color: colors.charcoal,
    lineHeight: '16px',
    paddingLeft: '11px',
  }),
  valueContainer: provided => ({
    ...provided,
    ...(iconDecoration
      ? {
          backgroundImage: `
            url('data:image/svg+xml;utf8,${encodeURIComponent(`
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path fill="${colors.charcoal}" d="${iconPaths[iconDecoration]}" />
              </svg>
            `)}')
          `,
          backgroundPosition: '10px 50%',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '16px 16px',
          paddingLeft: '32px',
        }
      : {}),
  }),
});

/**
 * An object in the shape required by the ReactSelect replacing components API.
 *
 * @see https://react-select.com/components
 */
const customComponents = {
  // eslint-disable-next-line react/prop-types
  ClearIndicator: ({ onClick, ...props }) => (
    <components.ClearIndicator {...props}>
      <ClearButton onClick={onClick} />
    </components.ClearIndicator>
  ),
  DropdownIndicator: props => (
    <components.DropdownIndicator {...props}>
      <Icon color="#A7A6A5" icon="caret" size="small" />
    </components.DropdownIndicator>
  ),
};

/**
 * Returns an function that composes the internal theme of the ReactSelect element as required by
 * the ReactSelect custom theme API.
 *
 * @see https://react-select.com/styles#overriding-the-theme
 */
const customTheme = size => reactSelectTheme => ({
  ...reactSelectTheme,
  borderRadius: '2px',
  spacing: {
    ...reactSelectTheme.spacing,
    controlHeight: sizes[size].height || size,
    menuGutter: '4px',
  },
});

/**
 * @deprecated, use Select controlled input
 *
 * This component was built originally for uncontrolled forms but now we are moving towards
 * controlled forms because they are a more common React pattern. For more info check the link.
 * @link https://benchco.atlassian.net/browse/IN-2122?utm_medium=referral-external&utm_source=jira-slack&utm_term=
 */
class ComboBoxInput extends Component {
  static propTypes = {
    defaultValue: PropTypes.shape({ label: PropTypes.string, value: PropTypes.any }),
    errorText: PropTypes.string,
    /** Add an icon to the left of the search input; provide the name of the icon from Icon.jsx */
    iconDecoration: PropTypes.oneOf(Object.keys(iconPaths)),
    onChange: PropTypes.func,
    /** Can be either 'small', 'medium', or any CSS height string, like '40px' */
    size: PropTypes.oneOf([...Object.keys(sizes), PropTypes.string]),
    required: PropTypes.bool,
  };

  static defaultProps = {
    defaultValue: undefined,
    errorText: undefined,
    iconDecoration: undefined,
    isSearchable: false,
    onChange: () => {},
    required: false,
    size: 'medium',
  };

  constructor(props) {
    super(props);

    this.inputComponentRef = React.createRef();
  }

  render() {
    const { defaultValue, errorText, iconDecoration, onChange, required, size } = this.props;
    const wasValidated =
      this.inputComponentRef.current && this.inputComponentRef.current.state.wasValidated;
    return (
      <div>
        <ReactSelect
          {...this.props}
          components={customComponents}
          defaultValue={defaultValue}
          onChange={selectedOption => {
            const validatedHtmlInput = this.inputComponentRef.current.inputRef.current;
            /**
             * Simulating a change event to trigger onChange
             * https://stackoverflow.com/questions/23892547/what-is-the-best-way-to-trigger-onchange-event-in-react-js
             */
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
              window.HTMLInputElement.prototype,
              'value',
            ).set;
            nativeInputValueSetter.call(
              validatedHtmlInput,
              selectedOption ? selectedOption.value : '',
            );
            const changeEvent = new Event('input', { bubbles: true });
            validatedHtmlInput.dispatchEvent(changeEvent);
            if (typeof onChange === 'function') {
              onChange(selectedOption);
            }
          }}
          styles={customStyles(iconDecoration, size, required, wasValidated, errorText)}
          theme={customTheme(size)}
        />
        <TextInputWrapper>
          <TextInput
            data-test-id="validated-input"
            data-visually-hidden
            defaultValue={defaultValue && defaultValue.value}
            onInvalid={() => this.setState({})}
            ref={this.inputComponentRef}
            required={required}
            tabIndex="-1"
          />
        </TextInputWrapper>
      </div>
    );
  }
}

export default ComboBoxInput;
